import React, { useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    DialogActions,
    DialogContent,
    Typography,
} from '@mui/material';
import { isNullOrUndefined } from '../../../utils/utils';
import { useTranslation } from 'react-i18next';
import { DialogComponentProps } from '../../../models/quotations/NewQutationDialogProps';
import PrimaryBtn from '../../shared/Buttons/PrimaryButton';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import useQuotationActionsDialog from '../../../hooks/dialogs/useQuotationActionsDialog';
import useQuotationSWR from '../../../hooks/quotations/useQuotationsSWR';
import TextFieldStyled from 'components/shared/TextFieldStyled';
import { QuotationData } from '../../../models/quotations/Quotations';
import { ErrorTypography } from '../../styled/ErrorTypography';
import { SelectLoadingLayout } from '../../layouts/LoadingLayouts/SelectLoadingLayout';
import useQuotation from '../../../hooks/quotations/useQuotation';

const AddToExistQuotation: React.FC<DialogComponentProps> = (props) => {
    if (isNullOrUndefined(props)) {
        return null;
    }
    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATION_DIALOG.EXIST_QUOTATION_DIALOG',
    });
    const { products, handleSave } = props;
    const quotation = useQuotation();
    const [saveIsLoading, setSaveIsLoading] = useState(false);
    const [selectedQuotationId, setSelectedQuotationId] =
        useState<number>(null);
    const quotationActionsDialog = useQuotationActionsDialog();
    const { quotes, isLoading, isError } = useQuotationSWR();

    const closeConfirmDialog = () => {
        quotationActionsDialog.closeDialog();
    };

    const handleCreatedSuccessfully = () => {
        SnackbarUtils.success(t('ADDED_SUCCESSFULLY').toString());
        handleSave(selectedQuotationId);
    };

    const onConfirmDialog = () => {
        setSaveIsLoading(true);
        quotation
            .addProductsToQuotation({
                products: {
                    items: quotation.convertProductsToQuotation(products),
                },
                quotationId: selectedQuotationId,
            })
            .then(() => {
                handleCreatedSuccessfully();
            })
            .finally(() => {
                setSaveIsLoading(false);
            });
    };

    const handleSelect = (event, newValue) => {
        setSelectedQuotationId(newValue ? newValue.id : null);
    };

    return (
        <>
            <DialogContent>
                <Box sx={{ paddingTop: '10px' }}>
                    <Typography paragraph>{t('SELECT_CONTENT')}</Typography>
                </Box>
                {isError && (
                    <ErrorTypography
                        errorCode={'ADD_TO_EXIST_QUOTATION_LOADING_ERROR'}
                    />
                )}
                {isLoading && <SelectLoadingLayout />}
                {!isLoading && quotes && (
                    <Autocomplete
                        onChange={(event: any, newValue: QuotationData) => {
                            handleSelect(event, newValue);
                        }}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        options={quotes}
                        renderInput={(params) => (
                            <TextFieldStyled
                                fullWidth
                                variant="outlined"
                                {...params}
                                label={t('SELECT_LABEL')}
                            />
                        )}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={closeConfirmDialog}>
                    {t('CANCEL')}
                </Button>
                <PrimaryBtn
                    size="small"
                    onClick={onConfirmDialog}
                    disabled={
                        isLoading ||
                        saveIsLoading ||
                        isNullOrUndefined(selectedQuotationId)
                    }
                >
                    {t('CONFIRM')}
                </PrimaryBtn>
            </DialogActions>
        </>
    );
};

export default AddToExistQuotation;
